// src/components/ReviewPage.js
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const ReviewPage = () => {
  const { bookingId } = useParams();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!rating || rating < 1 || rating > 5) {
      setError('Please select a rating before submitting.');
      return;
    }
    if (comment.length > 1000) {
      setError('Review must be under 1000 characters.');
      return;
    }

    setSubmitStatus('loading');
    try {
      await axios.post(`/review/${bookingId}`, {
        rating: parseInt(rating),
        comment: comment.trim(),
      });
      setSubmitStatus('success');
    } catch (err) {
      setSubmitStatus('error');
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    }
  };

  const styles = {
    root: {
      fontFamily: "'Jost', sans-serif",
      background: '#0A0A0A',
      color: '#fff',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '4rem 1.5rem',
    },
    card: {
      background: '#111',
      border: '1px solid rgba(201,168,76,0.3)',
      borderRadius: '12px',
      padding: '3rem',
      maxWidth: '600px',
      width: '100%',
    },
    eyebrow: {
      fontFamily: "'Jost', sans-serif",
      fontSize: '0.65rem',
      letterSpacing: '5px',
      textTransform: 'uppercase',
      color: '#C9A84C',
      marginBottom: '1rem',
      textAlign: 'center',
    },
    title: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(1.8rem, 5vw, 2.4rem)',
      fontWeight: 700,
      textAlign: 'center',
      color: '#fff',
      margin: '0 0 0.5rem',
    },
    subtitle: {
      fontSize: '0.85rem',
      color: 'rgba(255,255,255,0.5)',
      textAlign: 'center',
      margin: '0 0 2rem',
    },
    divider: {
      width: '50px',
      height: '2px',
      background: '#C9A84C',
      margin: '0 auto 2.5rem',
    },
    fieldLabel: {
      fontSize: '0.7rem',
      letterSpacing: '3px',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.5)',
      display: 'block',
      marginBottom: '1rem',
    },
    starsRow: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'center',
      marginBottom: '2.5rem',
    },
    starBtn: (active) => ({
      background: active ? 'rgba(201,168,76,0.1)' : 'none',
      border: `1px solid ${active ? '#C9A84C' : 'rgba(201,168,76,0.25)'}`,
      color: active ? '#C9A84C' : 'rgba(201,168,76,0.3)',
      width: '52px',
      height: '52px',
      borderRadius: '4px',
      fontSize: '1.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    textarea: {
      width: '100%',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: '4px',
      color: '#fff',
      fontFamily: "'Jost', sans-serif",
      fontSize: '0.9rem',
      lineHeight: '1.7',
      padding: '1rem',
      resize: 'vertical',
      minHeight: '140px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    charCount: {
      fontSize: '0.7rem',
      color: 'rgba(255,255,255,0.3)',
      textAlign: 'right',
      marginTop: '0.4rem',
    },
    errorBox: {
      background: 'rgba(220,50,50,0.08)',
      border: '1px solid rgba(220,50,50,0.3)',
      color: '#ff8080',
      fontSize: '0.83rem',
      padding: '0.8rem 1rem',
      borderRadius: '4px',
      marginBottom: '1.5rem',
    },
    submitBtn: (disabled) => ({
      background: disabled ? 'rgba(201,168,76,0.2)' : '#C9A84C',
      color: disabled ? 'rgba(201,168,76,0.5)' : '#0A0A0A',
      border: 'none',
      padding: '0.95rem',
      width: '100%',
      fontFamily: "'Jost', sans-serif",
      fontWeight: 600,
      fontSize: '0.8rem',
      letterSpacing: '3px',
      textTransform: 'uppercase',
      borderRadius: '3px',
      cursor: disabled ? 'default' : 'pointer',
      marginTop: '2rem',
      transition: 'background 0.2s',
    }),
  };

  if (submitStatus === 'success') {
    return (
      <>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@700&family=Jost:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <div style={styles.root}>
          <div style={styles.card}>
            <p style={styles.eyebrow}>Nairobi's Ultimate Experience</p>
            <h1 style={styles.title}>Thank You</h1>
            <div style={styles.divider} />
            <p style={{ color: 'rgba(255,255,255,0.65)', textAlign: 'center', fontSize: '0.95rem', lineHeight: 1.8, margin: 0 }}>
              Your review has been submitted. We're grateful for your feedback — it helps us craft even better experiences for future guests.
            </p>
            <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
              <a href="/" style={{ ...styles.submitBtn(false), display: 'inline-block', textDecoration: 'none', padding: '0.9rem 2.5rem', width: 'auto' }}>
                Back to Home
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  const displayRating = hoveredRating || rating;

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@700&family=Jost:wght@300;400;500;600&display=swap" rel="stylesheet" />
      <div style={styles.root}>
        <div style={styles.card}>
          <p style={styles.eyebrow}>Nairobi's Ultimate Experience</p>
          <h1 style={styles.title}>Share Your Experience</h1>
          <p style={styles.subtitle}>Your feedback helps us craft better journeys for every guest</p>
          <div style={styles.divider} />

          {error && <div style={styles.errorBox}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <label style={styles.fieldLabel}>Your Rating</label>
            <div style={styles.starsRow}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  style={styles.starBtn(star <= displayRating)}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  disabled={submitStatus === 'loading'}
                  aria-label={`${star} star${star > 1 ? 's' : ''}`}
                >
                  ★
                </button>
              ))}
            </div>

            <label style={styles.fieldLabel} htmlFor="comment">Your Review</label>
            <textarea
              id="comment"
              style={styles.textarea}
              rows={5}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did you enjoy most? We'd love to hear your story…"
              disabled={submitStatus === 'loading'}
              maxLength={1000}
            />
            <p style={styles.charCount}>{comment.length} / 1000</p>

            <button
              type="submit"
              style={styles.submitBtn(submitStatus === 'loading' || rating === 0)}
              disabled={submitStatus === 'loading' || rating === 0}
            >
              {submitStatus === 'loading' ? 'Submitting…' : 'Submit Review'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ReviewPage;